import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, SimpleGrid, Icon,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { ICON_MAP, getIconByKey, getWorkCentreIconByName } from './iconMap';
import api from '../api';
import ConfirmModal from './ConfirmModal';

const ICON_KEYS = Object.keys(ICON_MAP).sort();

function IconPickerGrid({ selected, onSelect }) {
  const [filter, setFilter] = useState('');
  const filtered = filter
    ? ICON_KEYS.filter(k => k.toLowerCase().includes(filter.toLowerCase()))
    : ICON_KEYS;

  return (
    <Box>
      <Input size="xs" placeholder="Search icons..." mb={2} value={filter}
        onChange={(e) => setFilter(e.target.value)} />
      <Box maxH="180px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={1}>
        <SimpleGrid columns={10} spacing={1}>
          {filtered.map(key => {
            const Ic = ICON_MAP[key];
            const isSelected = selected === key;
            return (
              <Flex key={key} w="32px" h="32px" align="center" justify="center" cursor="pointer"
                borderRadius="md" border="2px solid" bg={isSelected ? 'blue.500' : 'white'}
                borderColor={isSelected ? 'blue.500' : 'transparent'}
                color={isSelected ? 'white' : 'gray.600'}
                _hover={{ bg: isSelected ? 'blue.600' : 'gray.100', borderColor: isSelected ? 'blue.600' : 'gray.300' }}
                onClick={() => onSelect(isSelected ? null : key)}
                title={key.replace('Fi', '')}>
                <Icon as={Ic} boxSize={4} />
              </Flex>
            );
          })}
        </SimpleGrid>
      </Box>
      {selected && (
        <HStack mt={1} fontSize="10px" color="gray.500" spacing={1}>
          <Text>Selected:</Text>
          <Badge fontSize="9px" colorScheme="blue">{selected.replace('Fi', '')}</Badge>
          <Icon as={ICON_MAP[selected]} boxSize={3} />
        </HStack>
      )}
    </Box>
  );
}

export default function WorkCentresModal({ isOpen, onClose }) {
  const [confirmDel, setConfirmDel] = useState(null);
  const [centres, setCentres] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', icon: '', sort_order: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchCentres = useCallback(async () => {
    try { const { data } = await api.get('/work-centres'); setCentres(data); } catch(e) {}
  }, []);

  useEffect(() => { if (isOpen) { fetchCentres(); setShowForm(false); setEditingId(null); } }, [isOpen, fetchCentres]);

  const resetForm = () => setForm({ name: '', icon: '', sort_order: '' });

  const handleSave = async () => {
    if (!form.name) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, icon: form.icon || null, sort_order: parseInt(form.sort_order) || 0 };
      if (editingId) { await api.put(`/work-centres/${editingId}`, payload); }
      else { await api.post('/work-centres', payload); }
      await fetchCentres(); setShowForm(false); setEditingId(null); resetForm();
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: e.response?.data?.error || 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const openEdit = (wc) => {
    setForm({ name: wc.name, icon: wc.icon || getWorkCentreIconByName(wc.name) || '', sort_order: String(wc.sort_order || '') });
    setEditingId(wc.id); setShowForm(true);
  };

  const resolveIcon = (wc) => {
    if (wc.icon) return getIconByKey(wc.icon);
    const fallback = getWorkCentreIconByName(wc.name);
    return fallback ? getIconByKey(fallback) : null;
  };

  const doDeleteWc = async () => { if (confirmDel) { await api.delete(`/work-centres/${confirmDel.id}`); fetchCentres(); setConfirmDel(null); } };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="90vh">
        <ModalHeader fontSize="md">
          <Flex justify="space-between" pr={8}>Work Centres
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => { resetForm(); setEditingId(null); setShowForm(!showForm); }}>{showForm ? 'Cancel' : 'Add'}</Button>
          </Flex>
        </ModalHeader><ModalCloseButton />
        <ModalBody>
          {showForm && (
            <Box bg="blue.50" p={3} borderRadius="md" mb={4}>
              <VStack spacing={3}>
                <HStack w="100%" spacing={2}>
                  <FormControl flex={3}><FormLabel fontSize="xs" mb={0.5}>Name *</FormLabel>
                    <Input size="xs" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
                  <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Sort Order</FormLabel>
                    <Input size="xs" type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} /></FormControl>
                </HStack>
                <FormControl>
                  <FormLabel fontSize="xs" mb={0.5}>Icon</FormLabel>
                  <IconPickerGrid selected={form.icon} onSelect={(key) => setForm(p => ({ ...p, icon: key }))} />
                </FormControl>
                <Button size="xs" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                  onClick={handleSave} isLoading={saving} alignSelf="flex-end">{editingId ? 'Update' : 'Add'}</Button>
              </VStack>
            </Box>
          )}

          <VStack spacing={1} align="stretch">
            {centres.map(wc => {
              const Ic = resolveIcon(wc);
              return (
                <Flex key={wc.id} p={2} border="1px" borderColor="gray.100" borderRadius="md"
                  align="center" justify="space-between" _hover={{ bg: 'gray.50' }}>
                  <HStack spacing={3}>
                    <Flex w="32px" h="32px" align="center" justify="center" bg="gray.100" borderRadius="md">
                      {Ic ? <Icon as={Ic} boxSize={4} color="gray.700" /> : <Text fontSize="10px" color="gray.400">—</Text>}
                    </Flex>
                    <Box>
                      <Text fontSize="12px" fontWeight="600">{wc.name}</Text>
                      <HStack spacing={1} fontSize="9px" color="gray.400">
                        {wc.icon && <Badge fontSize="7px" colorScheme="blue">{wc.icon.replace('Fi', '')}</Badge>}
                        <Text>Order: {wc.sort_order || 0}</Text>
                      </HStack>
                    </Box>
                  </HStack>
                  <HStack spacing={0}>
                    <IconButton icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(wc)} aria-label="Edit" />
                    <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                      onClick={() => setConfirmDel(wc)}
                      aria-label="Delete" />
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
    <ConfirmModal isOpen={!!confirmDel} onClose={() => setConfirmDel(null)}
      title="Delete Work Centre" message={`Delete "${confirmDel?.name || ''}"?`}
      confirmLabel="Delete" confirmColor="red" onConfirm={doDeleteWc} />
    </>
  );
}
